import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  GalleryHorizontal, Search, X, Plus, ArrowLeft, Save, ArrowUp, ArrowDown,
  Eye, EyeOff, Pencil, Package, MessageSquare, AlertCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { extractErrorMessage } from '../../utils/errorHelpers';
import PhotoUploader from '../../components/PhotoUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = { title: '', subtitle: '', image_url: '', cta_label: '', cta_link: '' };

const AdminBanner = () => {
  const { hasPermission } = useAuth();
  const canAccess = hasPermission('featured');
  const navigate = useNavigate();

  const [slides, setSlides] = useState([]);
  const [maxCount, setMaxCount] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [mode, setMode] = useState('message'); // 'message' | 'product'
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!canAccess) navigate('/connexion');
  }, [canAccess, navigate]);

  const fetchState = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/banner`);
      setSlides(res.data.slides || []);
      setMaxCount(res.data.max_count || 10);
      setDirty(false);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/products?search=${encodeURIComponent(q)}`);
        setResults((res.data || []).slice(0, 15));
      } catch (e) { /* silent */ }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const markDirty = () => setDirty(true);

  const addProductSlide = (product) => {
    if (slides.length >= maxCount) { toast.warning(`Maximum ${maxCount} slides`); return; }
    setSlides((prev) => [...prev, {
      id: null, type: 'product', product_id: product.id, product,
      title: '', subtitle: '', image_url: '', cta_label: '', cta_link: '', active: true,
    }]);
    setQuery('');
    setResults([]);
    markDirty();
  };

  const submitMessageForm = () => {
    if (!form.title.trim()) { toast.error('Le titre est obligatoire'); return; }
    if (editingId !== null) {
      setSlides((prev) => prev.map((s, i) => (i === editingId ? { ...s, ...form } : s)));
      setEditingId(null);
    } else {
      if (slides.length >= maxCount) { toast.warning(`Maximum ${maxCount} slides`); return; }
      setSlides((prev) => [...prev, { id: null, type: 'message', active: true, ...form }]);
    }
    setForm(EMPTY_FORM);
    markDirty();
  };

  const editSlide = (idx) => {
    const s = slides[idx];
    setMode('message');
    setEditingId(idx);
    setForm({
      title: s.title || '', subtitle: s.subtitle || '', image_url: s.image_url || '',
      cta_label: s.cta_label || '', cta_link: s.cta_link || '',
    });
  };

  const removeSlide = (idx) => {
    setSlides((prev) => prev.filter((_, i) => i !== idx));
    if (editingId === idx) { setEditingId(null); setForm(EMPTY_FORM); }
    markDirty();
  };

  const moveSlide = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= slides.length) return;
    setSlides((prev) => {
      const copy = [...prev];
      const [item] = copy.splice(idx, 1);
      copy.splice(to, 0, item);
      return copy;
    });
    markDirty();
  };

  const toggleActive = (idx) => {
    setSlides((prev) => prev.map((s, i) => (i === idx ? { ...s, active: !s.active } : s)));
    markDirty();
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/banner`, {
        slides: slides.map(({ product, product_missing, ...s }) => s),
      });
      toast.success('Bannière enregistrée');
      fetchState();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Échec de l'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16" data-testid="admin-banner-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link to="/admin" className="text-zinc-500 hover:text-[color:var(--color-primary)] text-xs flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Tableau de bord
          </Link>
          <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">Bannière d'accueil</p>
          <h1 className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Carrousel défilant de la page d'accueil
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Ajoutez des slides photo produit ou des messages importants (max {maxCount}). Défilement automatique toutes les 5 secondes.
          </p>
        </div>

        {slides.length === 0 && !loading && (
          <div className="border border-orange-500/40 bg-orange-500/10 p-4 mb-6 flex items-start gap-3" data-testid="banner-empty-warning">
            <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <p className="text-orange-300 text-sm">
              Aucune slide — la bannière n'apparaît pas sur l'accueil. Ajoutez au moins une slide ci-dessous.
            </p>
          </div>
        )}

        {loading ? (
          <p className="text-zinc-500 text-sm py-12 text-center">Chargement…</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Slides list */}
            <div className="lg:col-span-2 border border-white/10 bg-[color:var(--color-surface)] p-5" data-testid="banner-slides-list">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em]">Slides actuelles</p>
                <p className="text-zinc-400 text-xs"><span data-testid="banner-slides-count">{slides.length}</span> / {maxCount}</p>
              </div>

              {slides.length === 0 ? (
                <p className="text-zinc-600 text-sm italic py-8 text-center">Aucune slide. Ajoutez-en une ci-contre.</p>
              ) : (
                <ul className="space-y-2">
                  {slides.map((s, idx) => (
                    <SlideRow
                      key={s.id || `new-${idx}`}
                      slide={s} idx={idx} total={slides.length}
                      onMove={moveSlide} onToggle={toggleActive}
                      onEdit={editSlide} onRemove={removeSlide}
                    />
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-white/10">
                <button type="button" onClick={save} disabled={saving || !dirty}
                  data-testid="banner-save"
                  className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <Save className="w-3.5 h-3.5" /> Enregistrer la bannière
                </button>
                <p className="text-zinc-600 text-xs ml-auto">
                  {dirty ? 'Modifications non enregistrées' : 'Bannière à jour'}
                </p>
              </div>
            </div>

            {/* Add / edit panel */}
            <div className="lg:col-span-1 border border-white/10 bg-[color:var(--color-surface)] p-5" data-testid="banner-add-panel">
              <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-3">
                {editingId !== null ? `Modifier la slide #${editingId + 1}` : 'Ajouter une slide'}
              </p>

              {editingId === null && (
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button type="button" onClick={() => setMode('message')}
                    data-testid="banner-mode-message"
                    className={`flex items-center justify-center gap-2 py-2.5 text-xs uppercase tracking-wider border transition-colors ${
                      mode === 'message' ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> Message
                  </button>
                  <button type="button" onClick={() => setMode('product')}
                    data-testid="banner-mode-product"
                    className={`flex items-center justify-center gap-2 py-2.5 text-xs uppercase tracking-wider border transition-colors ${
                      mode === 'product' ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>
                    <Package className="w-3.5 h-3.5" /> Produit
                  </button>
                </div>
              )}

              {mode === 'product' && editingId === null ? (
                <div>
                  <div className="relative mb-3">
                    <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder="Nom, marque, code-barres…"
                      data-testid="banner-product-search"
                      className="w-full bg-black border border-white/10 text-white pl-10 pr-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none" />
                  </div>
                  {results.length > 0 && (
                    <ul className="max-h-[400px] overflow-y-auto divide-y divide-white/5 border border-white/5">
                      {results.map((p) => (
                        <li key={p.id}>
                          <button type="button" onClick={() => addProductSlide(p)}
                            data-testid={`banner-add-product-${p.id}`}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition-colors text-left">
                            {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 object-cover bg-white/5 flex-shrink-0" />}
                            <div className="min-w-0 flex-1">
                              <p className="text-white text-xs truncate">{p.name}</p>
                              <p className="text-zinc-500 text-[10px] truncate">{p.brand}</p>
                            </div>
                            <Plus className="w-4 h-4 text-[color:var(--color-primary)]" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {query.trim().length >= 2 && results.length === 0 && (
                    <p className="text-zinc-600 text-xs italic">Aucun produit trouvé</p>
                  )}
                  <p className="text-zinc-600 text-[11px] mt-3">
                    La photo, le nom et la marque du produit sont utilisés automatiquement, avec un bouton « Découvrir » vers sa fiche.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1">Titre *</label>
                    <input type="text" value={form.title}
                      onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="Ex : Livraison offerte dès 500€"
                      data-testid="banner-form-title"
                      className="w-full bg-black border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1">Sous-titre (optionnel)</label>
                    <input type="text" value={form.subtitle}
                      onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
                      placeholder="Ex : Valable jusqu'au 30 juin"
                      data-testid="banner-form-subtitle"
                      className="w-full bg-black border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1">Image de fond (optionnel)</label>
                    <PhotoUploader
                      currentImage={form.image_url}
                      onUpload={(url) => setForm((f) => ({ ...f, image_url: url }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-zinc-400 text-xs block mb-1">Texte du bouton</label>
                      <input type="text" value={form.cta_label}
                        onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))}
                        placeholder="En savoir plus"
                        data-testid="banner-form-cta-label"
                        className="w-full bg-black border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs block mb-1">Lien du bouton</label>
                      <input type="text" value={form.cta_link}
                        onChange={(e) => setForm((f) => ({ ...f, cta_link: e.target.value }))}
                        placeholder="/catalogue"
                        data-testid="banner-form-cta-link"
                        className="w-full bg-black border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none" />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button type="button" onClick={submitMessageForm}
                      data-testid="banner-form-submit"
                      className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] transition-colors">
                      <Plus className="w-3.5 h-3.5" />
                      {editingId !== null ? 'Appliquer' : 'Ajouter la slide'}
                    </button>
                    {editingId !== null && (
                      <button type="button"
                        onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}
                        data-testid="banner-form-cancel"
                        className="border border-white/10 text-zinc-400 px-4 py-2 text-xs uppercase tracking-wider hover:border-white/30 transition-colors">
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SlideRow = ({ slide, idx, total, onMove, onToggle, onEdit, onRemove }) => {
  const isProduct = slide.type === 'product';
  const thumb = slide.image_url || (isProduct ? slide.product?.image_url : null);
  const title = slide.title || (isProduct ? slide.product?.name : '') || '(sans titre)';
  const sub = slide.subtitle || (isProduct ? slide.product?.brand : '');

  return (
    <li className={`flex items-center gap-3 border bg-black p-2.5 ${slide.active ? 'border-white/5' : 'border-white/5 opacity-50'}`}
        data-testid={`banner-slide-row-${idx}`}>
      <span className="text-[color:var(--color-primary)] text-xs w-5 text-right">{idx + 1}</span>
      <div className="flex flex-col gap-0.5">
        <button type="button" onClick={() => onMove(idx, -1)} disabled={idx === 0}
          data-testid={`banner-move-up-${idx}`}
          className="text-zinc-600 hover:text-[color:var(--color-primary)] disabled:opacity-30 p-0.5">
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => onMove(idx, 1)} disabled={idx === total - 1}
          data-testid={`banner-move-down-${idx}`}
          className="text-zinc-600 hover:text-[color:var(--color-primary)] disabled:opacity-30 p-0.5">
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
      </div>
      {thumb ? (
        <img src={thumb} alt="" className="w-14 h-10 object-cover bg-white/5 flex-shrink-0" />
      ) : (
        <div className="w-14 h-10 bg-gradient-to-br from-zinc-900 to-[color:rgb(var(--primary-rgb)/0.20)] flex-shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${
            isProduct ? 'border-purple-500/40 text-purple-300' : 'border-cyan-500/40 text-cyan-300'}`}>
            {isProduct ? 'Produit' : 'Message'}
          </span>
          {slide.product_missing && (
            <span className="text-[9px] uppercase text-red-400 border border-red-500/40 px-1.5 py-0.5">Produit supprimé</span>
          )}
        </div>
        <p className="text-white text-sm truncate mt-0.5">{title}</p>
        {sub && <p className="text-zinc-500 text-xs truncate">{sub}</p>}
      </div>
      <button type="button" onClick={() => onToggle(idx)}
        data-testid={`banner-toggle-${idx}`}
        title={slide.active ? 'Masquer' : 'Afficher'}
        className={`p-1.5 ${slide.active ? 'text-emerald-400' : 'text-zinc-600'} hover:text-[color:var(--color-primary)]`}>
        {slide.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
      {!isProduct && (
        <button type="button" onClick={() => onEdit(idx)}
          data-testid={`banner-edit-${idx}`}
          className="text-zinc-500 hover:text-[color:var(--color-primary)] p-1.5">
          <Pencil className="w-4 h-4" />
        </button>
      )}
      <button type="button" onClick={() => onRemove(idx)}
        data-testid={`banner-remove-${idx}`}
        className="text-zinc-600 hover:text-red-400 p-1.5">
        <X className="w-4 h-4" />
      </button>
    </li>
  );
};

export default AdminBanner;
